// React
import {useCallback, useEffect, useState} from 'react';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, useMediaQuery } from '@mui/material';

// Hooks
import { useAppTheme } from '../../../hooks/useAppTheme.js';

// Store
import useProfileStore from '../../../store/profileStore.js';
import useSiteStore from '../../../store/siteStore.js';
import useCoachStore from '../../../store/coachStore.js';
import useAthleteStore from '../../../store/athleteStore.js';

// Style
import { darkTheme } from '../../../styles/colorSchemes/index.js';

// Pages
import NationalCoachHome from './nationalCoachHome.js';
import LoadingProgress from '../../../components/custom/LoadingProgress.js';
import NationalCoachHomeMobile from './nationalCoachHomeMobile.js';
import CoachHomeMobile from './coachHomeMobile.js';
import CoachHome from './coachHome.js';

// Other
import { getAllAthletes } from '../../../service/httpService/athlete/index.js';
import { addWeeklySchedule, getWeeklyScheduleOfAthlete } from '../../../service/httpService/weeklySchedule/index.js';
import { getAllCoaches } from '../../../service/httpService/coach/index.js';
import { getAllParents } from '../../../service/httpService/parent/index.js';
import { getAllFeedbacksOfWeekOfAthlete } from '../../../service/httpService/feedback/index.js';
import { getAthletesOfCoach } from '../../../service/httpService/athlete/index.js';
import { getParentsOfCoach } from '../../../service/httpService/coach/index.js';
import { addTextblock, updateTextblock } from '../../../service/httpService/textblocks/index.js';
import { formatDate, formatCalendarWeekString, getISOWeekStart, getWeek, getWindowSize }  from '../../../utils/index.js';

function safeISOWeekStart(week, year, weekDayIndex) {
    const simple = new Date(year, 0, 4);
    const dow = simple.getDay() || 7; 
    const isoStart = new Date(simple);

    isoStart.setDate(simple.getDate() - dow + 1 + (week - 1) * 7);

    const result = new Date(isoStart);
    result.setDate(isoStart.getDate() + weekDayIndex);

    return result;
}
function getDateFromWeek(year, week) {
    const simple = new Date(year, 0, 1 + (week - 1) * 7);
    const dow = simple.getDay();
    const monday = new Date(simple);
    monday.setDate(simple.getDate() - ((dow <= 4 ? dow : dow - 7) - 1));
    return monday;
}
const DashboardCoach = () => {
    const { profileType, userEmail } = useProfileStore();
    const { isDarkMode } = useAppTheme();
    const { allOpen, isLoading, mobile, openWeeklySchedule, selectedCalenderWeek, selectedTab, setAllOpen, setCalendarWeekString, setFailMessage, setFeedbacks, setLoading, 
      setOpenWeeklySchedule, setSelectedCalenderWeek, setSnackbarOpenFail, setSnackbarOpenSuccess, setSuccessMessage, setTableRowsOpen, setWeeklySchedule, setWeeklyScheduleLoading, 
      tableRowsOpen, weeklySchedule, year, setYear, setSelectedAthlete: setSelectedAthleteInStore, 
                        selectedViewAthletesTab, setSelectedViewAthletesTab, setSelectedTab, pendingOpenTrainingSessionId, setPendingOpenTrainingSessionId } = useSiteStore();
    const { checkedAthletes, linkMessage, repetitions, setCheckedAthletes, setWeeklyScheduleRevisionCounter, 
      setWindowSize, weeklyScheduleRevisionCounter, windowSize, } = useCoachStore();
    const { setTrainingSessions, trainingSessions } = useAthleteStore();
    const isTablet = useMediaQuery('(min-width:601px) and (max-width:1000px)');

    // Use mobile-like layout for both mobile and tablet
    const compactLayout = mobile || isTablet;

    const [parents, setParents] = useState(); 
    const [athletes, setAthletes] = useState([]); 
    const [selectedAthlete, setSelectedAthlete] = useState(JSON.parse(localStorage.getItem('selectedAthlete'))); 
    const [coaches, setCoaches] = useState();
    const [textblocks, setTextblocks] = useState([]);
    const [openSwitchAthleteDialog, setOpenSwitchAthleteDialog] = useState(false);
    const [pendingAthlete, setPendingAthlete] = useState(null);
    
    const primaryColor = isDarkMode ? '#ff9800' : '#1976d2';
    const [pendingIsMobile, setPendingIsMobile] = useState(false);
    const [pendingNavigateDate, setPendingNavigateDate] = useState(null);

    useEffect(() => {
     const savedSessions = localStorage.getItem('trainingSessions');

if (savedSessions) {
    try {
        const parsed = JSON.parse(savedSessions);

        if (Array.isArray(parsed)) {
            setTrainingSessions(parsed);
        } else {
            console.warn("trainingSessions im LocalStorage war kein Array → gelöscht");
            localStorage.removeItem('trainingSessions');
            setTrainingSessions([]);
        }

    } catch (e) {
        console.error('Failed to parse saved training sessions', e);
        localStorage.removeItem('trainingSessions');
        setTrainingSessions([]);
    }
}

    }, [setTrainingSessions]);

    useEffect(() => {
        if (trainingSessions.length > 0) {
            localStorage.setItem('trainingSessions', JSON.stringify(trainingSessions));
        }
    }, [trainingSessions]);

    // Callback to refetch athletes, coaches, and parents after edit dialog updates
    const refetchUserLists = useCallback(() => {
        const cFuncs = [getAthletesOfCoach, getParentsOfCoach];
        const cStates = [setAthletes, setParents];
        
        const ncFuncs = [getAllAthletes, getAllCoaches, getAllParents];
        const ncStates = [setAthletes, setCoaches, setParents];
        
        if(profileType === 'coach') {
            for(let i = 0; i < cFuncs.length; i++) {
                cFuncs[i](userEmail).then(response => {
                    cStates[i](response.data);
                }).catch();
            }
            return;
        }
        
        for(let i = 0; i < ncFuncs.length; i++) {
            ncFuncs[i]().then(response => {
                ncStates[i](response.data);
            }).catch();
        }
    }, [profileType, userEmail]);
   
    useEffect(() => {
        localStorage.setItem('selectedCalenderWeek', JSON.stringify(selectedCalenderWeek));
    }, [selectedCalenderWeek]);

    useEffect(() => {
        localStorage.setItem('selectedAthlete', JSON.stringify(selectedAthlete));
        setCheckedAthletes([selectedAthlete]);
        
    }, [selectedAthlete, setCheckedAthletes]);

    useEffect(() => {
        localStorage.setItem('selectedTab', JSON.stringify(selectedTab));
    }, [selectedTab]);

    useEffect(() => {
        localStorage.setItem('openWeeklySchedule_mobile', JSON.stringify(openWeeklySchedule));
    }, [openWeeklySchedule]);

    useEffect(() => {
        localStorage.setItem('selectedViewAthletesTab', JSON.stringify(selectedViewAthletesTab));
    }, [selectedViewAthletesTab]);

    useEffect(() => {
        function handleWindowResize() {
            setWindowSize(getWindowSize());
        }

        window.addEventListener('resize', handleWindowResize);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
    }, [setWindowSize]);

    /*
    * Diese Funktion holt alle wichtigen Daten für das Dashboard
    * Wenn der Benutzer ein Trainer ist, holt sie die Athleten und Eltern des Trainers
    * Wenn der Benutzer ein Nationaltrainer ist, holt sie alle Athleten, Trainer und Eltern
    */
    useEffect(() => {
      refetchUserLists();
      setLoading(false);
    }, [profileType, userEmail, refetchUserLists, setLoading]);

    useEffect(() => {
        if (weeklySchedule === undefined || weeklySchedule.trainingSessions === undefined) return;

        const nextLength = weeklySchedule.trainingSessions.length;

        // While a notification auto-open is pending, do not reset all rows to closed/open.
        // This initializer can otherwise race with the auto-open effect in WeeklyScheduleTable.
        if (pendingOpenTrainingSessionId) {
            setTableRowsOpen((prev) => {
                const prevArr = Array.isArray(prev) ? prev : [];
                if (prevArr.length === nextLength) return prevArr;
                const resized = [...prevArr];
                while (resized.length < nextLength) resized.push(false);
                return resized.slice(0, nextLength);
            });
            return;
        }

        setTableRowsOpen((prev) => {
            const prevArr = Array.isArray(prev) ? prev : [];
            const alreadyInitialized = prevArr.length === nextLength;
            if (alreadyInitialized) return prevArr;

            const values = [];
            for (let i = 0; i < nextLength; i++) values.push(allOpen);
            return values;
        });
    }, [weeklySchedule, allOpen, setTableRowsOpen, pendingOpenTrainingSessionId]);

    const getWeeklySchedule = useCallback((email) => {
        setWeeklyScheduleLoading(true);

        const schedulePromise = getWeeklyScheduleOfAthlete(email, year, selectedCalenderWeek)
            .then(response => response.data)
            .catch(() => undefined);

        const feedbackPromise = getAllFeedbacksOfWeekOfAthlete(email, year, selectedCalenderWeek)
            .then(response => response.data)
            .catch(() => []);

        Promise.all([schedulePromise, feedbackPromise]).then(([scheduleData, feedbackData]) => {
            setWeeklySchedule(scheduleData);
            setFeedbacks(feedbackData);
            setWeeklyScheduleLoading(false);
        });
    }, [selectedCalenderWeek, setFeedbacks, setWeeklySchedule, setWeeklyScheduleLoading, year]);

    useEffect(() => {
        if (selectedAthlete !== null) {
            getWeeklySchedule(selectedAthlete.profile.email);
        }
        setCalendarWeekString(formatCalendarWeekString(selectedCalenderWeek, year));
    }, [weeklyScheduleRevisionCounter, selectedCalenderWeek, selectedAthlete, year, getWeeklySchedule, setCalendarWeekString]); 

const handleChangeTrainingSession = (index, value, property) => {
    let values = Array.isArray(trainingSessions) ? [...trainingSessions] : [];

    if (!values[index]) return;

    // Dauer prüfen
    if (property === 'duration') {
        if (value < 0 || String(value).length > 3) {
            return;
        }
    }

    // Fokus 7/8/9 → Dauer + Intensität nullen
    if (property === 'trainingFocus') {
        if (value === 8 || value === 7 || value === 9) {
            values[index].duration = 0;
            values[index].intensity = 0;
        }
    }

    // Wochentag → Datum neu berechnen
    if (property === 'dayOfWeek') {
        const monday = getDateFromWeek(year, selectedCalenderWeek);
        const newDate = new Date(monday);
        newDate.setDate(monday.getDate() + (value - 1));
        values[index].date = newDate;
    }

    // Wert tatsächlich setzen (WICHTIG!)
    values[index][property] = value;

    setTrainingSessions(values);
};


    const prepCreateWeeklySchedule = (e) => {
		// Only treat it as a calendar event if it has a date property
		if(e && e.date) {
			// Check if any athletes are selected
			if (!selectedAthlete || selectedAthlete.length === 0) {
				setFailMessage("Bitte wählen Sie zuerst einen Athleten aus.");
				setSnackbarOpenFail(true);
				return;
			}
			let date = new Date(e.date);
			const weekFromDate = getWeek(date);
			
			// Calculate the ISO week year (may differ from calendar year for dates like Dec 31)
			// ISO week year: if Jan 1-3 is in week 52/53, it belongs to previous year; if Dec 29-31 is in week 1, it belongs to next year
			const tempDate = new Date(date.getTime());
			tempDate.setDate(tempDate.getDate() + 3 - (tempDate.getDay() + 6) % 7);
			const isoWeekYear = tempDate.getFullYear();
			
			setYear(isoWeekYear);
			setSelectedCalenderWeek(weekFromDate);
			onChangeSelectedViewAthletesTab("createWeeklySchedule");
			let weekDay = date.getDay();
			initTrainingSession( date, weekDay === 0 ? 7 : weekDay);
			return;
		}

		// If coming from year calendar view (not a calendar event), reset to current week AND current year
		const currentDate = new Date();
		const currentWeek = getWeek(currentDate);
		const currentYear = currentDate.getFullYear();
		
		const weekToUse = selectedViewAthletesTab === 'calendarYear' ? currentWeek : selectedCalenderWeek;
		if (selectedViewAthletesTab === 'calendarYear') {
			setYear(currentYear);
			setSelectedCalenderWeek(weekToUse);
		}
		
		onChangeSelectedViewAthletesTab("createWeeklySchedule");
		initTrainingSession( getISOWeekStart(weekToUse, selectedViewAthletesTab === 'calendarYear' ? currentYear : year, 0), 1 );
    };

const onChangeStartWeek = (valueOrEvent) => {
    let newWeek = valueOrEvent?.target
        ? Number(valueOrEvent.target.value)
        : Number(valueOrEvent);

    if (!newWeek || newWeek < 1 || newWeek > 52) return;

    // Update selected calendar week
    setSelectedCalenderWeek(newWeek);

    const monday = getDateFromWeek(year, newWeek);

    // vorhandene Trainingseinheiten auf neue KW schieben
    setTrainingSessions(prev =>
        Array.isArray(prev)
            ? prev.map((session) => {
                  if (!session) return session;
                  const d = new Date(monday);
                  d.setDate(monday.getDate() + (session.dayOfWeek - 1));
                  return { ...session, date: d };
              })
            : []
    );
};


const addTrainingSession = () => {
    setTrainingSessions(prevSessions => {
        const sessions = Array.isArray(prevSessions) ? prevSessions : [];

        // Wenn es schon Sessions gibt → nächsten Tag berechnen
        if (sessions.length > 0) {
            const last = sessions[sessions.length - 1];

            // nächsten Wochentag berechnen (1–7)
            const nextDow = last.dayOfWeek === 7 ? 1 : last.dayOfWeek + 1;

            // Montag der KW
            const monday = getDateFromWeek(year, selectedCalenderWeek);

            // Neues Datum
            const nextDate = new Date(monday);
            nextDate.setDate(monday.getDate() + (nextDow - 1));

            const newSession = {
                dayOfWeek: nextDow,
                trainingFocus: "",
                duration: 0,
                description: "",
                intensity: 0,
                date: nextDate,
                isGlobal: false
            };

            return [...sessions, newSession];
        }

        // ERSTE Session → Montag der KW
        const monday = getDateFromWeek(year, selectedCalenderWeek);

        const firstSession = {
            dayOfWeek: 1,
            trainingFocus: "",
            duration: 0,
            description: "",
            intensity: 0,
            date: monday,
            isGlobal: false
        };

        return [firstSession];
    });
};



  const initTrainingSession = (initDate, dow) => {

    const validDate = initDate instanceof Date && !isNaN(initDate)
        ? initDate
        : safeISOWeekStart(selectedCalenderWeek, year, dow - 1);

    const newSessions = [{
        dayOfWeek: dow,
        trainingFocus: '',
        duration: 0,
        description: '',
        intensity: 0,
        date: validDate,
        isGlobal: false
    }];

    setTrainingSessions(newSessions);
};


    const handleToggleAthlete = (value) => {
		const currentIndex = checkedAthletes.indexOf(value);
		const newChecked = [...checkedAthletes];
		if (currentIndex !== -1) {
			newChecked.splice(currentIndex, 1);
		}
    else {
      newChecked.push(value);
    }

		setCheckedAthletes(newChecked);
    };

    const onChangeSelectedViewAthletesTab = useCallback(( view ) => {                
		if(view !== "toggleCalendar"){
			setSelectedViewAthletesTab( view );
			return;
		}
		
		// When switching from year view to week view, reset to current year/week
		if (selectedViewAthletesTab === "calendarYear") {
			const currentDate = new Date();
			const currentWeek = getWeek(currentDate);
			const currentYear = currentDate.getFullYear();
			setYear(currentYear);
			setSelectedCalenderWeek(currentWeek);
		}
		
		setSelectedViewAthletesTab(selectedViewAthletesTab === "calendarWeek" ? "calendarYear" : "calendarWeek");
    }, [selectedViewAthletesTab, setSelectedViewAthletesTab, setYear, setSelectedCalenderWeek]);

    // Navigate to weekly schedule view (WeeklyScheduleTable) for a given date
    const goToWeeklySchedule = useCallback((e) => {
        if(e && e.date) {
            let date = new Date(e.date);
            const weekFromDate = getWeek(date);
			
            // Calculate the ISO week year
            const tempDate = new Date(date.getTime());
            tempDate.setDate(tempDate.getDate() + 3 - (tempDate.getDay() + 6) % 7);
            const isoWeekYear = tempDate.getFullYear();
			
            setYear(isoWeekYear);
            setSelectedCalenderWeek(weekFromDate);
            onChangeSelectedViewAthletesTab("calendarWeek");
        }
    }, [onChangeSelectedViewAthletesTab, setSelectedCalenderWeek, setYear]);

    const handleSelectAthlete = useCallback((athlete, isMobile) =>{
		if(selectedViewAthletesTab === "createWeeklySchedule") {
			// Show confirmation dialog before switching
			setPendingAthlete(athlete);
			setPendingIsMobile(isMobile);
			setOpenSwitchAthleteDialog(true);
			return;
		}

		setSelectedAthlete(athlete);
		setSelectedAthleteInStore(athlete);
		getWeeklySchedule(athlete.profile.email);
		if (isMobile) {
			setOpenWeeklySchedule(true);
		}
		onChangeSelectedViewAthletesTab( "calendarYear" );
    }, [selectedViewAthletesTab, getWeeklySchedule, setSelectedAthleteInStore, onChangeSelectedViewAthletesTab, setOpenWeeklySchedule]);

    const confirmSwitchAthlete = () => {
		if (pendingAthlete) {
			onChangeSelectedViewAthletesTab("calendarYear");
			setSelectedAthlete(pendingAthlete);
			setSelectedAthleteInStore(pendingAthlete);
			getWeeklySchedule(pendingAthlete.profile.email);
			if (pendingIsMobile) {
				setOpenWeeklySchedule(true);
			}
            if (pendingNavigateDate) {
                goToWeeklySchedule({ date: pendingNavigateDate });
                setTimeout(() => {
                    document.getElementById('weekly-schedule-table')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 50);
                setPendingNavigateDate(null);
            }
		}
		setOpenSwitchAthleteDialog(false);
		setPendingAthlete(null);
		setPendingIsMobile(false);
    };

    const cancelSwitchAthlete = () => {
		setOpenSwitchAthleteDialog(false);
		setPendingAthlete(null);
		setPendingIsMobile(false);
        setPendingNavigateDate(null);
    };

    useEffect(() => {
        const handler = (event) => {
            const detail = event?.detail || {};
            const athleteEmail = detail?.athleteEmail;
            const rawDate = detail?.date;
            const trainingSessionId = detail?.trainingSessionId;
            if (!athleteEmail || !rawDate) return;

            setSelectedTab(0);

            const dateObj = new Date(rawDate);
            if (Number.isNaN(dateObj.getTime())) return;

            const athleteFromList = athletes?.find(a => a?.profile?.email === athleteEmail);
            const fallbackAthlete = {
                profile: {
                    email: athleteEmail,
                    firstName: detail?.athlete?.firstName || '',
                    lastName: detail?.athlete?.lastName || '',
                    picture: null,
                }
            };
            const athlete = athleteFromList || fallbackAthlete;

            // If user is currently creating a weekly schedule, reuse the existing confirmation flow.
            if (selectedViewAthletesTab === 'createWeeklySchedule') {
                setPendingNavigateDate(dateObj);
                handleSelectAthlete(athlete, !!detail?.isMobile);
                if (trainingSessionId) setPendingOpenTrainingSessionId(trainingSessionId);
                return;
            }

            handleSelectAthlete(athlete, !!detail?.isMobile);
            goToWeeklySchedule({ date: dateObj });
            if (trainingSessionId) setPendingOpenTrainingSessionId(trainingSessionId);
            setTimeout(() => {
                document.getElementById('weekly-schedule-table')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 50);
        };

        window.addEventListener('simon:openWeeklyScheduleFromNotification', handler);
        return () => window.removeEventListener('simon:openWeeklyScheduleFromNotification', handler);
    }, [athletes, handleSelectAthlete, goToWeeklySchedule, selectedViewAthletesTab, setSelectedTab, setPendingOpenTrainingSessionId]);

    const openAndCloseAll = () => {
		let values = [...tableRowsOpen];
		for (let i = 0; i < values.length; i++) {
			values[i] = !allOpen;
		}
		setTableRowsOpen(values);
		setAllOpen(!allOpen);
    }

    const openAndCloseTableRow = (index) => {
		let values = [...tableRowsOpen];
		values[index] = !values[index];
		setTableRowsOpen(values);
    }



    const handleSendTextblock = async (index, isGlobal) => {
      let t = trainingSessions[index];
      let textblockNew = undefined;

      if (t.trainingFocus <= 0) {
        setSnackbarOpenFail(true);
        setFailMessage(`Bitte wählen Sie einen Trainingsfokus aus.`);
        return;
      }

      if (t.description.length === 0) {
        setSnackbarOpenFail(true);
        setFailMessage(`Bitte geben Sie eine Beschreibung an.`);
        return;
      }

      let textblock = {
        email: userEmail,
        isglobal: isGlobal,
        textblock: t.description,
        trainingfocus: t.trainingFocus,
      };

      try {
        textblockNew = await addTextblock(textblock).then(response => {
          return response.data;
        });
      } catch (err) {
        setFailMessage(`Textbaustein konnte nicht erstellt werden!`);
        setSnackbarOpenFail(true);
        return;
      }
      setSuccessMessage(`Textbaustein wurde erstellt!`);
      setSnackbarOpenSuccess(true);
      return textblockNew;
    }

		const handlePutTextblock = async(index, id) => {
      let t = trainingSessions[index];

      if (t.trainingFocus <= 0) {
        setSnackbarOpenFail(true);
        setFailMessage(`Bitte wählen Sie einen Trainingsfokus aus.`);
        return;
      }

      if (t.description.length === 0) {
        setSnackbarOpenFail(true);
        setFailMessage(`Bitte geben Sie eine Beschreibung an.`);
        return;
      }

      let textblock = {
        textblock: t.description,
        trainingfocus: t.trainingFocus,
      };

      try {
        await updateTextblock(id, textblock);;
      } catch (err) {
        setFailMessage(`Textbaustein konnte nicht geändert werden!`);
        setSnackbarOpenFail(true);
        return;
      }

      setSuccessMessage('Textbaustein wurde geändert!')
      setSnackbarOpenSuccess(true);
  }

    /**
     * erstellt/added Trainingsplan mit den Trainingsdaten vom Athleten
     * prüft dabei alle Werte auf die Richtigkeit
     */
    const handleSend = async () => {
		// Get current week and year for comparison
		const currentDate = new Date();
		const currentWeek = getWeek(currentDate);
		const currentYear = currentDate.getFullYear();
		
		// Check if selected week is in the past (considering year context)
		// Allow weeks in current or future years
		const isWeekInPast = year < currentYear || (year === currentYear && selectedCalenderWeek < currentWeek);
		
		// Some years have 53 weeks, check dynamically
		const dec31 = new Date(year, 11, 31);
		const maxWeeksInYear = getWeek(dec31) === 1 ? 52 : getWeek(dec31);
		
		if (isWeekInPast || selectedCalenderWeek > maxWeeksInYear || selectedCalenderWeek < 1) {
			setSnackbarOpenFail(true);
			setFailMessage("Fehlerhafte Kalenderwoche!");
			return;
		}
		
		if (repetitions <= 0 || repetitions >= 54 || repetitions > (52 - selectedCalenderWeek + 1)) {
			setSnackbarOpenFail(true);
			setFailMessage("Fehlerhafte Wiederholungen!");
			return;
		}
		
		if (checkedAthletes.length === 0) {
			setSnackbarOpenFail(true);
			setFailMessage("Kein Athlet ausgewählt!");
			return;
		}
	
		if (trainingSessions.length === 0) {
			setSnackbarOpenFail(true);
			setFailMessage("Keine Trainingseinheit vorhanden!");
			return;
		}
	
		for (let t of trainingSessions) {
			if (t.dayOfWeek <= 0) {
				setSnackbarOpenFail(true);
				setFailMessage(`Fehlerhafte Trainingseinheit (${formatDate(t.date)}): Wochentag!`);
				return;
			}

			if (t.trainingFocus <= 0) {
				setSnackbarOpenFail(true);
				setFailMessage(`Fehlerhafte Trainingseinheit (${formatDate(t.date)}): Fokus!`);
				return;
			}

			if (t.trainingFocus !== 8 && t.trainingFocus !== 7 && t.trainingFocus !== 9) {
				if (t.duration <= 0) {
					setSnackbarOpenFail(true);
					setFailMessage(`Fehlerhafte Trainingseinheit (${formatDate(t.date)}): Dauer!`);
					return;
				}

				if (t.intensity <= 0) {
					setSnackbarOpenFail(true);
					setFailMessage(`Fehlerhafte Trainingseinheit (${formatDate(t.date)}): Intensität!`);
					return;
				}
			}

			if (t.description.length === 0) {
				setSnackbarOpenFail(true);
				setFailMessage(`Fehlerhafte Trainingseinheit (${formatDate(t.date)}): Beschreibung!`);
				return;
			}
		}
	
		let calendarWeek = parseInt(selectedCalenderWeek, 10);
	
		for (let i = 0; i < repetitions; i++) {
			let emails = checkedAthletes.map(a => a.profile.email);

			for (let session of trainingSessions) {
				session.duration = parseInt(session.duration, 10);
				session.date = getISOWeekStart(calendarWeek, year, session.dayOfWeek);
			}

			let weeklySchedule = {
				emails: emails,
				calendarWeek: calendarWeek,
				year: year,
				trainingSessions: trainingSessions,
			};

			try {
				await addWeeklySchedule(weeklySchedule);
			} catch (err) {
				setFailMessage(`Wochenplan KW ${calendarWeek} konnte nicht hinzugefügt werden!`);
				setSnackbarOpenFail(true);
				return;
			}

			calendarWeek++;
		}
	
		onChangeSelectedViewAthletesTab("calendarYear");
		setWeeklyScheduleRevisionCounter(weeklyScheduleRevisionCounter + 1); 
		refetchUserLists(); // Refresh athlete statuses after creating schedules
		localStorage.removeItem('trainingSessions');
		//window.location.reload();
    };

    if (isLoading) return (<LoadingProgress/>);
    
    if(profileType === 'coach') {
        if (compactLayout) {
            return (
                <>
                    <Dialog 
                        open={openSwitchAthleteDialog} 
                        onClose={cancelSwitchAthlete}
                        PaperProps={{
                            sx: {
                                borderRadius: 3,
                                boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.5)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
                                minWidth: { xs: '90%', sm: '400px' },
                                bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                            }
                        }}
                    >
                        <DialogTitle sx={{ pb: 1, fontSize: '1.25rem', fontWeight: 600, borderBottom: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : '1px solid rgba(0, 0, 0, 0.08)', color: isDarkMode ? darkTheme.textPrimary : undefined }}>
                            Athletenwechsel bestätigen
                        </DialogTitle>
                        <DialogContent sx={{ pt: 3, pb: 2 }}>
                            <DialogContentText sx={{ fontSize: '0.95rem', color: isDarkMode ? darkTheme.textSecondary : 'text.secondary', lineHeight: 1.6 }}>
                                Wenn Sie zu einem anderen Athleten wechseln, gehen alle nicht gespeicherten Änderungen am Wochenplan verloren. Möchten Sie fortfahren?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                            <Button onClick={cancelSwitchAthlete} variant="outlined" sx={{ textTransform: 'none', borderRadius: 2, px: 3, borderColor: isDarkMode ? darkTheme.cardBorder : 'rgba(0, 0, 0, 0.23)', color: isDarkMode ? darkTheme.textPrimary : undefined, '&:hover': { borderColor: primaryColor, bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.08)' : 'rgba(25, 118, 210, 0.04)' } }}>Abbrechen</Button>
                            <Button onClick={confirmSwitchAthlete} variant="contained" color="error" sx={{ textTransform: 'none', px: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)', '&:hover': { boxShadow: '0 6px 16px rgba(211, 47, 47, 0.4)' } }}>Wechseln</Button>
                        </DialogActions>
                    </Dialog>
                    <CoachHomeMobile
                        athletes={ athletes }
                        selectedAthlete={ selectedAthlete }
                        checkedAthletes={ checkedAthletes }
                        handleSelectAthlete={ handleSelectAthlete }
                        parents={ parents }
                        repetitions={ repetitions }
                        startWeek={ selectedCalenderWeek }             
                        onChangeStartWeek={ onChangeStartWeek }
                        handleToggleAthlete={ handleToggleAthlete } 
                        handleChangeTrainingSession={ handleChangeTrainingSession }
                        addTrainingSession={ addTrainingSession }
                        handleSend={ handleSend }
                        linkMessage={ linkMessage }
                        onRefreshUserLists={ refetchUserLists }
                        prepCreateWeeklySchedule={ prepCreateWeeklySchedule }
                        textblocks={ textblocks }
                        setTextblocks={ setTextblocks }
                        handleSendTextblock={ handleSendTextblock }
                        handlePutTextblock={ handlePutTextblock }
                    />
                </>
            );
        }

        return (
            <>
                <Dialog open={openSwitchAthleteDialog} onClose={cancelSwitchAthlete} PaperProps={{ sx: { borderRadius: 3, boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.5)' : '0 8px 32px rgba(0, 0, 0, 0.12)', minWidth: { xs: '90%', sm: '400px' }, bgcolor: isDarkMode ? darkTheme.cardBg : 'white', } }}>
                    <DialogTitle sx={{ pb: 1, fontSize: '1.25rem', fontWeight: 600, borderBottom: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : '1px solid rgba(0, 0, 0, 0.08)', color: isDarkMode ? darkTheme.textPrimary : undefined }}>Athletenwechsel bestätigen</DialogTitle>
                    <DialogContent sx={{ pt: 3, pb: 2 }}><DialogContentText sx={{ fontSize: '0.95rem', color: isDarkMode ? darkTheme.textSecondary : 'text.secondary', lineHeight: 1.6 }}>Wenn Sie zu einem anderen Athleten wechseln, gehen alle nicht gespeicherten Änderungen am Wochenplan verloren. Möchten Sie fortfahren?</DialogContentText></DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                        <Button onClick={cancelSwitchAthlete} variant="outlined" sx={{ textTransform: 'none', borderRadius: 2, px: 3, borderColor: isDarkMode ? darkTheme.cardBorder : 'rgba(0, 0, 0, 0.23)', color: isDarkMode ? darkTheme.textPrimary : undefined, '&:hover': { borderColor: primaryColor, bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.08)' : 'rgba(25, 118, 210, 0.04)' } }}>Abbrechen</Button>
                        <Button onClick={confirmSwitchAthlete} variant="contained" color="error" sx={{ textTransform: 'none', px: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)', '&:hover': { boxShadow: '0 6px 16px rgba(211, 47, 47, 0.4)' } }}>Wechseln</Button>
                    </DialogActions>
                </Dialog>
                <CoachHome
                windowSize={ windowSize }
                athletes={ athletes }
                selectedAthlete={ selectedAthlete }
                repetitions={ repetitions }
                startWeek={ selectedCalenderWeek }
                year={ year }
                parents={ parents }
                checkedAthletes={ checkedAthletes }
                linkMessage={ linkMessage }
                openAndCloseTableRow={ openAndCloseTableRow }
                openAndCloseAll= {openAndCloseAll }
                handleToggleAthlete={ handleToggleAthlete }
                handleSelectAthlete={ handleSelectAthlete }
                handleChangeTrainingSession={ handleChangeTrainingSession }
                addTrainingSession={ addTrainingSession }
                handleSend={ handleSend }
                onChangeStartWeek={ onChangeStartWeek }
                selectedViewAthletesTab={ selectedViewAthletesTab }
                onChangeSelectedViewAthletesTab={ onChangeSelectedViewAthletesTab }
                prepCreateWeeklySchedule={ prepCreateWeeklySchedule }
                goToWeeklySchedule={ goToWeeklySchedule }
                textblocks={ textblocks }
                setTextblocks={ setTextblocks }
                handleSendTextblock={ handleSendTextblock }
                handlePutTextblock={ handlePutTextblock }
                onRefreshUserLists={ refetchUserLists } 
                trainingSessions={trainingSessions}
                weeklyScheduleRevisionCounter={weeklyScheduleRevisionCounter}/>
            

            </>
        )
    }

    //? NationalCoachHome View
    if (compactLayout) {
        return (
            <>
                <Dialog open={openSwitchAthleteDialog} onClose={cancelSwitchAthlete} PaperProps={{ sx: { borderRadius: 3, boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.5)' : '0 8px 32px rgba(0, 0, 0, 0.12)', minWidth: { xs: '90%', sm: '400px' }, bgcolor: isDarkMode ? darkTheme.cardBg : 'white', } }}>
                    <DialogTitle sx={{ pb: 1, fontSize: '1.25rem', fontWeight: 600, borderBottom: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : '1px solid rgba(0, 0, 0, 0.08)', color: isDarkMode ? darkTheme.textPrimary : undefined }}>Athletenwechsel bestätigen</DialogTitle>
                    <DialogContent sx={{ pt: 3, pb: 2 }}><DialogContentText sx={{ fontSize: '0.95rem', color: isDarkMode ? darkTheme.textSecondary : 'text.secondary', lineHeight: 1.6 }}>Wenn Sie zu einem anderen Athleten wechseln, gehen alle nicht gespeicherten Änderungen am Wochenplan verloren. Möchten Sie fortfahren?</DialogContentText></DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                        <Button onClick={cancelSwitchAthlete} variant="outlined" sx={{ textTransform: 'none', borderRadius: 2, px: 3, borderColor: isDarkMode ? darkTheme.cardBorder : 'rgba(0, 0, 0, 0.23)', color: isDarkMode ? darkTheme.textPrimary : undefined, '&:hover': { borderColor: primaryColor, bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.08)' : 'rgba(25, 118, 210, 0.04)' } }}>Abbrechen</Button>
                        <Button onClick={confirmSwitchAthlete} variant="contained" color="error" sx={{ textTransform: 'none', px: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)', '&:hover': { boxShadow: '0 6px 16px rgba(211, 47, 47, 0.4)' } }}>Wechseln</Button>
                    </DialogActions>
                </Dialog>
                <NationalCoachHomeMobile
                coaches={ coaches }
                athletes={ athletes }
                selectedAthlete={ selectedAthlete }
                checkedAthletes={ checkedAthletes }
                handleSelectAthlete={ handleSelectAthlete }
                parents={ parents }
                repetitions={ repetitions }
                startWeek={ selectedCalenderWeek }
                year={ year }
                        onChangeStartWeek={ onChangeStartWeek }
                handleToggleAthlete={ handleToggleAthlete }
                handleChangeTrainingSession={ handleChangeTrainingSession }
                addTrainingSession={ addTrainingSession }
                handleSend={ handleSend }
                linkMessage={ linkMessage }
                onRefreshUserLists={ refetchUserLists } 
                prepCreateWeeklySchedule={ prepCreateWeeklySchedule }
                textblocks={ textblocks }
                setTextblocks={ setTextblocks }
                handleSendTextblock={ handleSendTextblock }
                handlePutTextblock={ handlePutTextblock }
                />
            </>
        )
    }
    
    return (
        <>
            <Dialog open={openSwitchAthleteDialog} onClose={cancelSwitchAthlete} PaperProps={{ sx: { borderRadius: 3, boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.5)' : '0 8px 32px rgba(0, 0, 0, 0.12)', minWidth: { xs: '90%', sm: '400px' }, bgcolor: isDarkMode ? darkTheme.cardBg : 'white', } }}>
                <DialogTitle sx={{ pb: 1, fontSize: '1.25rem', fontWeight: 600, borderBottom: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : '1px solid rgba(0, 0, 0, 0.08)', color: isDarkMode ? darkTheme.textPrimary : undefined }}>Athletenwechsel bestätigen</DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 2 }}><DialogContentText sx={{ fontSize: '0.95rem', color: isDarkMode ? darkTheme.textSecondary : 'text.secondary', lineHeight: 1.6 }}>Wenn Sie zu einem anderen Athleten wechseln, gehen alle nicht gespeicherten Änderungen am Wochenplan verloren. Möchten Sie fortfahren?</DialogContentText></DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={cancelSwitchAthlete} variant="outlined" sx={{ textTransform: 'none', borderRadius: 2, px: 3, borderColor: isDarkMode ? darkTheme.cardBorder : 'rgba(0, 0, 0, 0.23)', color: isDarkMode ? darkTheme.textPrimary : undefined, '&:hover': { borderColor: primaryColor, bgcolor: isDarkMode ? 'rgba(255, 152, 0, 0.08)' : 'rgba(25, 118, 210, 0.04)' } }}>Abbrechen</Button>
                    <Button onClick={confirmSwitchAthlete} variant="contained" color="error" sx={{ textTransform: 'none', px: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)', '&:hover': { boxShadow: '0 6px 16px rgba(211, 47, 47, 0.4)' } }}>Wechseln</Button>
                </DialogActions>
            </Dialog>
            <NationalCoachHome
            coaches={ coaches }
            windowSize={ windowSize }
            athletes={ athletes }
            selectedAthlete={ selectedAthlete }
            repetitions={ repetitions }
            startWeek={ selectedCalenderWeek }
            year={ year }
            parents={ parents }
            checkedAthletes={ checkedAthletes }
            linkMessage={ linkMessage }
            openAndCloseTableRow={ openAndCloseTableRow }
            openAndCloseAll={ openAndCloseAll }
            handleToggleAthlete={ handleToggleAthlete }
            handleSelectAthlete={ handleSelectAthlete } 
            handleChangeTrainingSession={ handleChangeTrainingSession }
            addTrainingSession={ addTrainingSession }
            handleSend={ handleSend }
            onChangeStartWeek={ onChangeStartWeek }
            selectedViewAthletesTab={ selectedViewAthletesTab }
            onChangeSelectedViewAthletesTab={ onChangeSelectedViewAthletesTab }
            prepCreateWeeklySchedule={ prepCreateWeeklySchedule }
            goToWeeklySchedule={ goToWeeklySchedule }
            textblocks={ textblocks }
            setTextblocks={ setTextblocks }
            handleSendTextblock={ handleSendTextblock }
            handlePutTextblock={ handlePutTextblock }
            onRefreshUserLists={ refetchUserLists }
            weeklyScheduleRevisionCounter={ weeklyScheduleRevisionCounter }/>
        </>
    )
}

export default DashboardCoach;